/**
 * Flashka sound core — the platform-free public API every call site uses,
 * routing Flashka events to uisfx cues through a platform adapter:
 *
 *   - web:    `flashka-web/src/lib/sound` — the real uisfx player (runtime
 *             recipe synthesis via Web Audio, gesture unlock, voice limits)
 *   - mobile: `flashka-app/src/lib/sound` — the package's pre-rendered
 *             `uisfx/sounds/<pack>/*.mp3` exports of the same recipes,
 *             decoded once and played through `buffer-engine.ts` on
 *             react-native-audio-api
 *
 * Call sites fire `playSound('<event>')`; unconfigured, muted, cooled-down, or
 * pre-unlock calls are silent no-ops, so callers never need guards. Sound
 * reinforces visual/ARIA feedback — never replaces it.
 *
 * Every sound is a one-shot: there are no looping beds (see `cues.ts`), so
 * there is no handle registry to leak. Cues that belong to a SCREEN rather
 * than to a tap go through {@link playSoundAfterCurrent}, which chains them
 * onto the end of whatever is already sounding so the pair reads as two
 * distinct sounds instead of a chord.
 *
 * This module must stay free of runtime platform imports (`uisfx` appears
 * only as types): it is reachable from shared barrels that server-side
 * bundles (party worker / Hono) may touch.
 */

import type { PackName } from 'uisfx';

import { EVENT_CUES, type SoundEvent, type SoundSource } from './cues';

/**
 * A playing sound that can be stopped.
 *
 * `ended` resolves when the sound has finished playing — it is what
 * {@link playSoundAfterCurrent} chains on. Adapters that cannot report the end
 * of a sound may omit it; sequencing then degrades to "play immediately",
 * never to "never play".
 */
export type SoundHandle = { stop: () => void; ended?: Promise<void> };

/**
 * What each platform implements. `play` is fire-and-forget and must never
 * throw; it may return `null` when playback is suppressed (muted, voice
 * limit, web audio still locked) — callers treat a null handle as "nothing
 * to stop".
 */
export interface SoundPlayerAdapter {
  /**
   * Play a catalog cue or a local asset (see `SoundSource`). Local assets
   * exist for sounds uisfx has no cue for, e.g. the card reveal.
   */
  play(source: SoundSource, opts: { gain?: number }): SoundHandle | null;
  /** Immediately silence everything. */
  stopAll(): void;
  setEnabled(enabled: boolean): void;
  /** Master volume, 0..1. */
  setVolume(volume: number): void;
  /** Resume/unlock the audio stack from a user gesture (web); no-op native. */
  unlock(): void;
  /**
   * Switch the active uisfx pack. Both platforms implement it — web
   * synthesizes each pack's recipes at runtime, mobile bundles every pack's
   * pre-rendered exports — and the Settings pack selector drives it on both.
   * Optional only so an adapter that ships a single pack stays valid; the
   * committed default is `DEFAULT_SOUND_PACK`.
   */
  setPack?(pack: PackName): void;
}

export type SoundConfig = {
  adapter: SoundPlayerAdapter;
  /** Global mute gate — read on every play so toggling applies instantly. */
  isEnabled: () => boolean;
};

let adapter: SoundPlayerAdapter | null = null;
let isEnabled: (() => boolean) | null = null;
const lastPlayedAt = new Map<SoundEvent, number>();

/**
 * Resolves when the most recently started sound has finished — the tail of the
 * playback chain. Every `playSound` replaces it (a tap must never wait);
 * `playSoundAfterCurrent` extends it, so screen cues queue behind the action
 * cue that opened the screen and behind each other.
 */
let tail: Promise<void> = Promise.resolve();

/**
 * Ceiling on how long a sequenced cue will wait. Every cue in the catalog is
 * comfortably under a second, so a handle whose `ended` never settles (a
 * suspended AudioContext, a backgrounded app) must not swallow the next cue
 * outright.
 */
const MAX_WAIT_MS = 2_500;

/** `handle.ended`, bounded by {@link MAX_WAIT_MS} and never rejecting. */
function whenEnded(handle: SoundHandle | null): Promise<void> {
  const ended = handle?.ended;
  if (!ended) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, MAX_WAIT_MS);
    void ended
      .catch(() => {})
      .then(() => {
        // Drop the guard as soon as the sound really ends — a pending timer
        // per played cue would keep timers alive for no reason.
        clearTimeout(timer);
        resolve();
      });
  });
}

/**
 * Runtime cue reassignments, keyed by event name. EMPTY in production — this
 * exists so the Sound Cues Storybook page can retune the mapping live through
 * the REAL player instead of a parallel demo path. Overrides are by cue name,
 * so they survive pack switches; the story exports the resolved map to be
 * committed back into `cues.ts`.
 */
const cueOverrides = new Map<SoundEvent, SoundSource>();

/** Reassign (or, with `null`, restore) what an event plays. */
export function setCueOverride(target: SoundEvent, source: SoundSource | null): void {
  if (source === null) cueOverrides.delete(target);
  else cueOverrides.set(target, source);
}

export function clearCueOverrides(): void {
  cueOverrides.clear();
}

export function getCueOverrides(): Record<string, SoundSource> {
  return Object.fromEntries(cueOverrides);
}

/** What an event plays right now — the registry entry unless overridden. */
export function resolveEventSource(event: SoundEvent): SoundSource {
  const override = cueOverrides.get(event);
  if (override) return override;
  const def = EVENT_CUES[event];
  return def.asset !== undefined
    ? { kind: 'asset', asset: def.asset }
    : { kind: 'cue', cue: def.cue };
}

/** Configure the sound system. Call once at app startup; later calls replace the config. */
export function configureSound(config: SoundConfig): void {
  adapter = config.adapter;
  isEnabled = config.isEnabled;
  // A fresh player means nothing is sounding, so no sequenced cue should be
  // left waiting on the previous one's chain.
  tail = Promise.resolve();
}

/**
 * Play a Flashka sound event (see the event → cue map in `cues.ts`).
 * Fire-and-forget and always safe: no-ops when unconfigured or muted, and
 * drops plays inside the event's cooldown window — notification and
 * progress events are throttled hard so bursts never machine-gun. Events
 * with `cooldownMs: 0` (`typing`) are never throttled.
 */
export function playSound(event: SoundEvent): void {
  tail = whenEnded(playNow(event));
}

/**
 * The play itself. Returns the handle so the caller can chain on its end;
 * `null` whenever the play was suppressed (unconfigured, muted, or inside the
 * event's cooldown window).
 */
function playNow(event: SoundEvent): SoundHandle | null {
  if (!adapter || !isEnabled || !isEnabled()) return null;
  const def = EVENT_CUES[event];
  const now = Date.now();
  const last = lastPlayedAt.get(event);
  if (last !== undefined && now - last < def.cooldownMs) return null;
  lastPlayedAt.set(event, now);
  return adapter.play(resolveEventSource(event), { gain: def.gain });
}

/**
 * Play `event` once everything currently sounding has finished — for cues that
 * announce a SCREEN rather than a tap.
 *
 * A screen cue is triggered by the same interaction that already played its
 * own cue (the rating that emptied the queue, the answer that finished the
 * quiz), so firing on mount lands the two on the same beat and they read as
 * one muddled chord. Chaining off the previous sound's end makes them two
 * distinct sounds, in order, always.
 *
 * Fire-and-forget like {@link playSound}, and gated at PLAY time rather than
 * call time: muting or navigating away during the wait leaves it silent, and
 * the cooldown is measured against the moment it actually sounds.
 */
export function playSoundAfterCurrent(event: SoundEvent): void {
  if (!adapter) return;
  tail = tail
    .then(() => whenEnded(playNow(event)))
    .catch(() => {
      // A broken link must not stall every later cue behind it.
    });
}

/**
 * Push a prefs change into the player. Muting is IMMEDIATE: everything
 * currently sounding is cut, and the adapter is disabled so in-flight async
 * cues die too.
 */
export function applySoundsEnabled(enabled: boolean): void {
  if (!adapter) return;
  if (!enabled) {
    adapter.stopAll();
    // Nothing is sounding any more, so a cue queued after this point should
    // not sit behind a dead chain. Anything ALREADY queued still re-checks the
    // mute gate when its turn comes, so it stays silent.
    tail = Promise.resolve();
  }
  adapter.setEnabled(enabled);
}

/** Switch the active pack (web only — see `SoundPlayerAdapter.setPack`). */
export function applySoundPack(pack: PackName): void {
  adapter?.setPack?.(pack);
}

/** Push a master-volume change (0..1) into the player. */
export function applySoundVolume(volume: number): void {
  adapter?.setVolume(volume);
}

/**
 * Unlock audio from a real user gesture (web autoplay policy). The web
 * adapter also self-installs first-gesture listeners; this stays available
 * for belt-and-braces calls inside click handlers. No-op on native.
 */
export function unlockAudio(): void {
  adapter?.unlock();
}
